export const randomInsert = (originalArray, insertArray) => {
  // Return originalArray if it has fewer than 10 items or insertArray is empty
  if (originalArray.length < 10 || insertArray.length === 0) {
    return originalArray;
  }

  // Clone the originalArray to avoid modifying it
  let tempOriginalFeed = [...originalArray];

  let tempInsertFeed = [...insertArray];

  // Minimum distance between insertions
  const minDistance = 4;
  // Maximum distance between insertions (new constraint)
  const maxDistance = 8;
  // Target interval for reset items
  const resetInterval = 6;
  // Track occupied indices to enforce spacing
  let occupiedIndices = [];

  // Helper function to check if an index is valid
  const isValidIndex = (index) => {
    // Never insert at the first position (index 0)
    if (index <= 0 || index >= tempOriginalFeed.length) return false;

    // Check minimum distance constraint
    const tooClose = occupiedIndices.some(
      (occupied) => Math.abs(occupied - index) < minDistance
    );
    if (tooClose) return false;

    // If there are already occupied indices, also check maximum distance constraint
    // Skip this check for the first few insertions to allow initial proper spacing
    if (occupiedIndices.length >= 2) {
      // Find the closest occupied index
      let closestDistance = Number.MAX_SAFE_INTEGER;
      for (const occupied of occupiedIndices) {
        const distance = Math.abs(occupied - index);
        if (distance < closestDistance) {
          closestDistance = distance;
        }
      }

      // If the closest occupied index is too far, this index is not valid
      if (closestDistance > maxDistance) return false;
    }

    return true;
  };

  // Helper function to get a random valid index within a range
  const getRandomValidIndex = (start, end) => {
    // Ensure start is at least 1 to avoid inserting at the beginning
    const validStart = Math.max(1, start);

    const validIndices = [];
    for (let i = validStart; i <= end; i++) {
      if (isValidIndex(i)) {
        validIndices.push(i);
      }
    }
    if (validIndices.length === 0) return -1;
    return validIndices[Math.floor(Math.random() * validIndices.length)];
  };

  // Step 1: Insert the second element of insertArray first (if it exists)
  if (tempInsertFeed.length > 1) {
    const secondElement = tempInsertFeed[1];
    // Spread the first insertion across the first half of the array for better distribution
    // But start at position 1 at the earliest (never 0)
    const firstIndex = getRandomValidIndex(
      1,
      Math.floor(tempOriginalFeed.length / 2)
    );
    if (firstIndex !== -1) {
      tempOriginalFeed.splice(firstIndex, 0, secondElement);
      occupiedIndices.push(firstIndex);
    }
  }

  // Step 2: Insert reset items (assume insertArray[0] is the reset item)
  const resetItem = tempInsertFeed[0] || null;
  if (resetItem) {
    let currentIndex = 1; // Start at position 1, not 0
    while (currentIndex < tempOriginalFeed.length) {
      // Aim for every ~8 indices, with slight randomness (±2)
      const targetIndex =
        currentIndex + resetInterval + Math.floor(Math.random() * 5 - 2);
      if (targetIndex >= tempOriginalFeed.length) break;
      const validIndex = getRandomValidIndex(
        Math.max(1, targetIndex - 2),
        Math.min(tempOriginalFeed.length - 1, targetIndex + 2)
      );
      if (validIndex !== -1) {
        tempOriginalFeed.splice(validIndex, 0, resetItem);
        occupiedIndices.push(validIndex);
      }
      currentIndex =
        validIndex !== -1
          ? validIndex + minDistance
          : targetIndex + minDistance;
    }
  }

  // Step 3: Insert remaining elements from insertArray
  const remainingInserts = tempInsertFeed.slice(2); // Skip first (reset) and second (already inserted)
  remainingInserts.forEach((ulid) => {
    // Divide the array into segments to ensure even distribution
    const segmentSize = Math.floor(
      tempOriginalFeed.length / (remainingInserts.length + 1)
    );
    const segmentIndex = Math.floor(
      occupiedIndices.length / (remainingInserts.length + 1)
    );
    const start = Math.max(1, segmentIndex * segmentSize); // Ensure minimum start is 1, not 0
    const end = Math.min(start + segmentSize, tempOriginalFeed.length - 1);
    const randomIndex = getRandomValidIndex(start, end);
    if (randomIndex !== -1) {
      tempOriginalFeed.splice(randomIndex, 0, ulid);
      occupiedIndices.push(randomIndex);
    }
  });

  return tempOriginalFeed;
};
